/**
 * **Kestner.** The only human in the game who is anybody, and the first face.
 *
 * Spec §3: *not a monster.* D-088 and D-092 made the corridor's workers
 * deliberately faceless — visored, cropped, nobody in particular — and it works,
 * which is exactly why this asset is the risky one. A face on a toon-shaded,
 * low-poly figure is one bad vertex from cartoon, which D-002 forbids, or from
 * uncanny, which is worse.
 *
 * `docs/beat-6-plan.md` sets three rules for him and they are art direction, not
 * implementation. All three are answered in geometry here:
 *
 * 1. **He reads at 64 px by brim, stoop and head angle — never by features.**
 *    The silhouette is a dark disc over a dark shoulder mass with a pale
 *    rectangle held at the chest, and thin legs under it. That is four marks and
 *    a player can tell him from a worker across twenty metres of rain, which is
 *    the only distance beat 6 ever shows him at. `ASSET=kestner npm run
 *    shots:asset` is passed on the silhouette sheet, as every character in this
 *    game has been.
 * 2. **No camera in beat 6 comes closer than 4 m.** So this mesh has **no
 *    features at all** — no eyes, no mouth, no brow. It has a *face*, which is
 *    a plane of skin and a jaw at an angle, and that is a different thing. The
 *    close framing is beat 11's, where his face is the entire content, and
 *    pacing the risk that way is free.
 * 3. **He is tired, not cruel, and the lever is posture.** The torso is tipped
 *    forward over the board, the weight is on one hip, and the head is down.
 *    Nothing about the shape is aggressive; he is a man at the end of a shift in
 *    the rain, doing paperwork. If he ever reads as a villain the fix is the
 *    stance, never the face.
 *
 * **The cape is a shoulder cape and not a full-length one, and that is the whole
 * of the anti-monster margin.** A wide-brimmed hat over a floor-length dark cone
 * is a horror silhouette in every visual language there is. Ending the oilskin
 * at mid-torso leaves his legs visible and thin below it, and a figure with
 * visible legs reads as a person standing in weather rather than as a shape.
 *
 * Two `surface()` kinds, `organic` and `mineral`, which is the constraint
 * `docs/backlog.md` carries out of beat 4's kit. **Not `fabric`** — that kind
 * carries `wearable: 1` and D-002 reserves the wear channel for her alone.
 */
import * as THREE from 'three';
import { collapseRigged } from '@/world/instancing';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';

export function part(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

/**
 * How far back the boots sit from the hip's own axis, in metres.
 *
 * The same 0.05 `worker.ts` carries, for the same reason and from the same
 * failure: a leg is not symmetric about its ankle, so a boot on the axis puts
 * the whole figure forward of its own origin and `asset.spec.ts` rejects it.
 * Here the cape dominates the Z bounds either way — see `CAPE` — but these are
 * the same boots the workers wear and they must not quietly become different
 * ones, so the number stays.
 */
const BOOT_BACK = 0.05;

/**
 * Where the hip pivots sit, and it is 2.5 mm higher than the workers' 0.92.
 *
 * **Not a stylistic choice: `asset.spec.ts` rejected him at 0.9 with "kestner
 * floats or sinks (min.y): −0.00215".** The free leg is tilted 0.075 rad
 * (see `build`), and tilting a box does not merely move it — a 125 mm-wide
 * boot sole rotated about its hip gains `halfWidth · sin θ` of vertical extent,
 * which is 4.7 mm here, and half of that goes below the floor.
 *
 * So the compensation is exactly that half: 0.0625 · sin(0.075) + 0.006 ·
 * cos(0.075) = 0.01067 against the untilted 0.006, a difference of 2.15 mm,
 * rounded up to 2.5 for margin. It is the same lesson `worker.ts` records in
 * `BOOT_BACK` — a contrapposto is geometry, and geometry has to be paid for.
 */
const HIP_Y = 0.9025;

/**
 * The oilskin cape: a truncated cone hanging from the shoulders, and the
 * largest thing on him in every axis.
 *
 * It is centred on the origin and it is what sets the mesh's **rear** extent,
 * which is the half of the centring rule that has no other candidate: nothing
 * else on him reaches backwards at all. Everything forward of the origin — the
 * brim, the board, and his hands on it — is balanced against this one number,
 * and the measured Z centre is 0.011 against a 0.03 tolerance.
 *
 * The bottom radius went 0.31 → 0.33 for exactly that reason and for no
 * aesthetic one. **The hem is the counterweight to the clipboard.**
 *
 * It hangs from gravity rather than from his shoulders, so it does **not** take
 * the torso's forward tilt. A cape that leant with him would read as stiff
 * board rather than as wet cloth, and it would also swing the Z bounds off
 * centre and fail the gate.
 */
const CAPE = { top: 0.24, bottom: 0.33, height: 0.34, centreY: 1.35 } as const;

/**
 * The contrapposto, in two numbers.
 *
 * The torso rides `shift` toward the weighted leg and the shoulder line tilts
 * `tilt` the other way. That is the whole of "weight on one hip", and it is
 * worth more to this character than any amount of detail: a figure standing
 * square on both feet reads as posted, and a man who has been posted is a
 * guard. Kestner is not a guard. He is doing paperwork in the rain.
 */
const STANCE = { shift: 0.035, tilt: 0.06 } as const;

/** Where the spine bends and where the shoulders hang. Both are anatomy. */
const WAIST_Y = 0.98;
const SHOULDER_Y = 1.4;
/**
 * Shoulders sit toward the BACK of a ribcage, not on its centre plane.
 * Anatomy, and it is also 50 mm of the Z centring the arms would otherwise owe:
 * everything they carry — forearm, hand, and the board they hold — moves with
 * them, while the cape behind him does not.
 */
const SHOULDER_Z = -0.05;

/** Head down at the board. The director eases this to `HEAD_UP` on the tell. */
const HEAD_REST = 0.34;

export function addBoot(
  group: THREE.Group,
  side: 'left' | 'right',
  bootMaterial: THREE.Material,
  soleMaterial: THREE.Material,
): void {
  const sole = part(new THREE.BoxGeometry(0.125, 0.012, 0.28), soleMaterial, `${side}-boot-sole`);
  sole.position.set(0, -0.894, 0.09 - BOOT_BACK);
  group.add(sole);

  const foot = part(new THREE.BoxGeometry(0.115, 0.055, 0.21), bootMaterial, `${side}-boot-foot`);
  foot.position.set(0, -0.8605, 0.055 - BOOT_BACK);
  group.add(foot);

  const toe = part(new THREE.SphereGeometry(1, 8, 4), bootMaterial, `${side}-boot-rounded-toe`);
  toe.scale.set(0.0575, 0.0275, 0.07);
  toe.position.set(0, -0.8605, 0.16 - BOOT_BACK);
  group.add(toe);

  const shaft = part(new THREE.CylinderGeometry(0.085, 0.075, 0.345, 12), bootMaterial, `${side}-boot-shaft`);
  shaft.position.set(0, -0.6725, -BOOT_BACK);
  group.add(shaft);
}

function leg(
  side: 'left' | 'right',
  x: number,
  trouser: THREE.Material,
  boot: THREE.Material,
  sole: THREE.Material,
): THREE.Group {
  const hip = new THREE.Group();
  hip.name = `kestner-hip-${side}`;
  hip.position.set(x, HIP_Y, 0);

  const upper = part(new THREE.CylinderGeometry(0.1, 0.115, 0.42, 12), trouser, `${side}-upper-leg`);
  upper.position.set(0, -0.21, 0);
  hip.add(upper);

  const lower = part(new THREE.CylinderGeometry(0.085, 0.095, 0.4, 12), trouser, `${side}-lower-leg`);
  lower.position.set(0, -0.61, 0);
  hip.add(lower);
  addBoot(hip, side, boot, sole);
  return hip;
}

/**
 * An arm, and both of his are forward and down because both of them are on the
 * board.
 *
 * There is no swing here and no rig for one. He does not walk in beat 6 and he
 * does not walk in beat 11 either — the entire character is a man who stays
 * where he is and writes down what goes past him.
 *
 * The group is named and kept as a group so that `collapseRigged`'s kept-pivot
 * list is the only thing standing between this and an animated arm. It is NOT
 * in that list: see the note there for the 44 draw calls that decision was
 * worth.
 */
function arm(side: 'left' | 'right', x: number, oilskin: THREE.Material, skin: THREE.Material): THREE.Group {
  const shoulder = new THREE.Group();
  shoulder.name = `kestner-shoulder-${side}`;
  shoulder.position.set(x, SHOULDER_Y, SHOULDER_Z);
  // Barely off vertical. The upper arm hangs; it is the ELBOW that does the work.
  shoulder.rotation.x = side === 'right' ? -0.24 : -0.2;
  // **And IN, toward the midline, because his hands have to be ON the board.**
  //
  // Without this they hung where the shoulders are — half a metre apart, either
  // side of a clipboard 0.23 m wide — so the pale board floated between two pale
  // hands touching nothing. Alex spotted it in the review frames; no gate in
  // this project can see it, because "is he holding that" is not a number.
  //
  // Euler order is XYZ, so this Z rotation is applied FIRST and swings the whole
  // chain inward before the forward bend; the upper arm ends up lying against
  // his ribs, which is where a man reading a board actually puts it.
  shoulder.rotation.z = side === 'right' ? -0.35 : 0.35;

  const upper = part(new THREE.CylinderGeometry(0.058, 0.064, 0.28, 12), oilskin, `${side}-upper-arm`);
  upper.position.set(0, -0.14, 0);
  shoulder.add(upper);

  // **The elbow, and the first draft did not have one.** Both arms were single
  // straight limbs rotated forward from the shoulder, and it fails twice at
  // once: it put his hands at y 0.92, twenty centimetres below the clipboard
  // they are supposed to be holding, *and* it reached them 0.43 m out in front
  // of his chest, which pushed the mesh's Z centre to 0.058 against a 0.03
  // tolerance and `asset.spec.ts` rejected the figure.
  //
  // Both are the same fact. A man reading a board holds it against himself; the
  // only way a straight arm gets its hand up to chest height is to point it out
  // in front of him, so no rotation of one segment was ever going to work.
  const elbow = new THREE.Group();
  elbow.name = `kestner-elbow-${side}`;
  elbow.position.set(0, -0.28, 0);
  elbow.rotation.x = side === 'right' ? -1.15 : -1.11;
  shoulder.add(elbow);

  const forearm = part(new THREE.CylinderGeometry(0.05, 0.057, 0.23, 12), oilskin, `${side}-forearm`);
  forearm.position.set(0, -0.115, 0);
  elbow.add(forearm);

  // Bare, and it is the only skin on him below the jaw. A man does not write in
  // gloves, and the two pale marks at the chest are half of what makes the
  // clipboard read as a thing being *used* rather than carried.
  const hand = part(new THREE.BoxGeometry(0.075, 0.1, 0.07), skin, `${side}-hand`);
  hand.position.set(0, -0.27, 0);
  elbow.add(hand);
  return shoulder;
}

/**
 * The head, on its own pivot, because the pivot IS the encounter.
 *
 * `docs/beat-6-plan.md` rule 3: the flash and the watcher are one event, and
 * what the player actually sees of that is this node rotating. Everything else
 * about Kestner could be a still image; this cannot.
 */
function head(oilskin: THREE.Material, skin: THREE.Material): THREE.Group {
  const neck = new THREE.Group();
  neck.name = 'kestner-neck';
  neck.position.set(0, 1.46, 0);
  neck.rotation.x = HEAD_REST;

  const column = part(new THREE.CylinderGeometry(0.055, 0.06, 0.09, 10), skin, 'kestner-neck-column');
  column.position.set(0, 0.035, 0);
  neck.add(column);

  const skull = part(new THREE.SphereGeometry(1, 12, 8), skin, 'kestner-head');
  skull.scale.set(0.104, 0.126, 0.113);
  skull.position.set(0, 0.145, 0);
  neck.add(skull);

  // **The jaw, and it is one of the three things he is read by.**
  //
  // A sphere alone is a bald head and reads as a mannequin at any distance. A
  // box set forward and low under it gives the profile a chin, and a chin is
  // what tells a player which way a head is pointing — which is the entire
  // mechanical content of this character at the range beat 6 shows him.
  const jaw = part(new THREE.BoxGeometry(0.098, 0.062, 0.095), skin, 'kestner-jaw');
  jaw.position.set(0, 0.085, 0.045);
  neck.add(jaw);

  // The face is a PLANE, and there is nothing on it.
  //
  // Rule 2. It exists so the front of his head is not the same value as the
  // side of it — which is what stops a bare sphere reading as a helmet — and it
  // stops exactly there. Beat 11 is where features are affordable, because beat
  // 11 is the only beat that gets close enough for them to be the point.
  const face = part(new THREE.BoxGeometry(0.112, 0.105, 0.028), skin, 'kestner-face');
  face.position.set(0, 0.15, 0.09);
  neck.add(face);

  const crown = part(new THREE.CylinderGeometry(0.104, 0.115, 0.1, 14), oilskin, 'kestner-hat-crown');
  crown.position.set(0, 0.245, 0);
  neck.add(crown);

  // **The brim, and it is the mark he is read by first.**
  //
  // 0.40 m across, flat, all the way round — a sou'wester, not a cap peak. The
  // workers have a peak (`worker-cap-peak`, 0.19 × 0.09) and read as a column
  // with a bump; this reads as a disc on a stalk, and a disc survives being
  // twelve pixels tall. It is also simply what a man standing outside in a
  // thunderstorm with a paper form in his hands is wearing.
  const brim = part(new THREE.CylinderGeometry(0.2, 0.2, 0.016, 18), oilskin, 'kestner-hat-brim');
  brim.position.set(0, 0.198, 0.008);
  neck.add(brim);

  return neck;
}

/**
 * His materials, in one place because two meshes wear them.
 *
 * The seated variant in `kestnerSeated.ts` and the hat and cape on the office
 * hook are the same man and the same clothes; a second copy of these colours is
 * the first step to a hat on the wall that is not quite his hat.
 */
export function kestnerMaterials(): Record<'oilskin' | 'trouser' | 'boot' | 'sole' | 'skin' | 'board', THREE.Material> {
  const oilskin = surface('organic', 0x453e33);
  const trouser = surface('organic', 0x33353a);
  const boot = surface('organic', 0x1e2422);
  const sole = surface('organic', 0x14181a);
  // Desaturated and well down from a real complexion. It is the only skin in the
  // game and it must not out-compete her fabric; it is here to say "this is a
  // face and not a mask", which is a job that needs a value break and no more.
  const skin = surface('organic', 0x7d6d5c);
  // **The palest thing on him, on purpose.** He is frightening because he writes
  // things down, and the board is where that is said. Hardboard is `mineral`.
  const board = surface('mineral', 0x9a9384);
  return { oilskin, trouser, boot, sole, skin, board };
}

/**
 * The hat and the cape, off him, for the hook by the office door.
 *
 * `docs/beat-11-plan.md`: *"the hat is the role"*, so beat 11 seats him
 * without it — and his 64 px read is brim, stoop, head angle (rule 1 above).
 * The stoop carries on the seated figure; **the brim has to still be in the
 * room**, and this is where it is. Built from the same numbers `head()` and
 * `build()` use, so the hat on the wall is not a hat like his but his hat.
 *
 * Authored hanging from a hook at the origin, against a wall behind it (−Z):
 * the brim tipped against the plaster, the cape hanging flat under it rather
 * than as a cone, because an empty cape has nobody inside to hold it round.
 */
export function kestnerHatAndCape(): THREE.Group {
  const { oilskin } = kestnerMaterials();
  const group = new THREE.Group();
  group.name = 'kestner-hat-and-cape';

  const cape = part(
    new THREE.CylinderGeometry(CAPE.top, CAPE.bottom, CAPE.height, 16, 1, true),
    oilskin,
    'kestner-hook-cape',
  );
  // Flattened against the wall: the open cone squashed in Z to a hanging sheet.
  cape.scale.set(0.95, 1.25, 0.28);
  cape.position.set(0, -0.26, 0.05);
  group.add(cape);

  const hat = new THREE.Group();
  hat.position.set(0, 0.02, 0.1);
  // Hung by the brim's back edge, so it tips forward off the wall.
  hat.rotation.x = 1.25;
  const crown = part(new THREE.CylinderGeometry(0.104, 0.115, 0.1, 14), oilskin, 'kestner-hook-hat-crown');
  crown.position.set(0, 0.047, 0);
  hat.add(crown);
  const brim = part(new THREE.CylinderGeometry(0.2, 0.2, 0.016, 18), oilskin, 'kestner-hook-hat-brim');
  hat.add(brim);
  group.add(hat);
  return group;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'kestner';

  // Waxed brown, and it was authored a full stop darker than this — 0x2e2a22,
  // reasoned from "he must not out-compete her". **The frames said otherwise.**
  // At that value he was a black shape in a dark yard, in the one place in the
  // beat the plan describes as *"a man standing in the light making marks on a
  // board"*. A figure the player cannot see is not restrained, it is missing.
  //
  // The restraint that survives is the HUE. The workers' coveralls are cold
  // blue-grey (0x3c4550) under fluorescent; his is warm and wet under a sodium
  // flood, so the two men still read as different people at a glance, and she is
  // still the only pink thing anywhere in the frame.
  const { oilskin, trouser, boot, sole, skin, board } = kestnerMaterials();

  const hipLeft = leg('left', -0.13, trouser, boot, sole);
  const hipRight = leg('right', 0.13, trouser, boot, sole);
  // Weight on the right: that hip carries, so it stays under him and the left
  // leg is set out and slightly slack. Two rotations, and they are the reason
  // he does not read as posted.
  hipLeft.rotation.z = -0.075;
  hipRight.rotation.z = -0.015;

  const shoulderLeft = arm('left', -0.25, oilskin, skin);
  const shoulderRight = arm('right', 0.25, oilskin, skin);
  const neck = head(oilskin, skin);

  group.add(hipLeft, hipRight, shoulderLeft, shoulderRight, neck);

  // Tipped forward over the board and shifted onto the weighted hip. Everything
  // above the waist that is not rigged hangs off this, so the stoop is one
  // number rather than a number per mesh.
  //
  // **Its pivot is the WAIST, and the first draft's was the floor.** A group at
  // y 0 rotated 0.06 rad about Z does not tilt a torso 1.45 m up, it swings it
  // 87 mm sideways — which put the mesh's X centre at 0.025 against a 0.02
  // tolerance and, more to the point, made a lean look like a step. A man does
  // not bend at the ankles. Children are authored relative to `WAIST_Y` so the
  // two rotations happen where a spine actually is.
  const torso = new THREE.Group();
  torso.name = 'kestner-torso';
  torso.position.set(STANCE.shift, WAIST_Y, 0);
  torso.rotation.x = 0.1;
  torso.rotation.z = -STANCE.tilt;
  group.add(torso);

  const chest = part(new THREE.BoxGeometry(0.4, 0.5, 0.26), oilskin, 'kestner-chest');
  chest.position.set(0, 1.18 - WAIST_Y, 0);
  torso.add(chest);

  const yoke = part(new THREE.BoxGeometry(0.48, 0.08, 0.24), oilskin, 'kestner-shoulder-yoke');
  yoke.position.set(0, 1.45 - WAIST_Y, 0);
  torso.add(yoke);

  // Hung from the group and NOT from the torso — see `CAPE`. Open-ended so the
  // hem is a silhouette edge rather than a lid.
  const cape = part(
    new THREE.CylinderGeometry(CAPE.top, CAPE.bottom, CAPE.height, 16, 1, true),
    oilskin,
    'kestner-cape',
  );
  cape.position.set(0, CAPE.centreY, 0);
  group.add(cape);

  // The board itself, tipped up toward him so its face is not a flat rectangle
  // seen edge-on from the far side of the yard — which is where it is seen from
  // for the whole of beat 6.
  const clipboard = part(new THREE.BoxGeometry(0.23, 0.3, 0.012), board, 'kestner-clipboard');
  clipboard.position.set(0.02, 1.1, 0.25);
  clipboard.rotation.x = -0.75;
  group.add(clipboard);

  const clip = part(new THREE.BoxGeometry(0.1, 0.022, 0.018), board, 'kestner-clipboard-clip');
  clip.position.set(0.02, 1.19, 0.332);
  clip.rotation.x = -0.75;
  group.add(clip);

  // **Only the neck stays live, and the number is why.**
  //
  // With the hips and both shoulders kept as pivots too, this figure censused at
  // **56 draw calls** and put beat 6's worst frame at 305 against a ceiling of
  // 290 — one man costing more than the entire perimeter fence. `collapseRigged`
  // merges everything that is not under a kept pivot, and every kept pivot's
  // subtree stays as loose meshes: six per leg, four per arm, six in the head,
  // doubled again by the post stack's normal prepass.
  //
  // He does not walk in beat 6, and he does not walk in beat 11 either. The one
  // thing that moves is his head coming up on the tell, which IS the encounter
  // (see `head()`), so that is the one thing that stays a pivot. The stance
  // rotations above are applied first and bake into the merged geometry, so he
  // still stands on one hip.
  //
  // **Restoring an arm is one entry in this array**, on the day a beat actually
  // needs him to hold a gate. Until then it is 44 draw calls a frame for a pivot
  // nothing turns, in a room that has no walls to cull behind (D-027).
  collapseRigged(group, [neck]);

  return group;
}

export interface KestnerParts {
  /**
   * Rotate about X. This is the encounter — see `head()` — and it is the only
   * live pivot on him. Hips and shoulders were exposed and are not any more; see
   * the note at `collapseRigged` for what they cost and how to get one back.
   */
  readonly neck: THREE.Object3D;
}

function required(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (!found) throw new Error(`kestner: rig node "${name}" missing — was the mesh renamed?`);
  return found;
}

export function findKestnerParts(root: THREE.Object3D): KestnerParts {
  return { neck: required(root, 'kestner-neck') };
}

export const kestnerAsset: AssetDefinition = {
  id: 'kestner',
  label: 'Kestner',
  description: 'The stockman: oilskin cape, wide brim, clipboard. Tired, not cruel; no features.',
  // Measured off the built geometry rather than taken from a brief, the
  // discipline `worker.ts` records. The cape is the widest thing on him in both
  // horizontal axes at 2 × 0.31, which is also what keeps the mesh centred
  // without a compensation term; height is the crown of the hat, which the head
  // pivot's rest rotation tips very slightly forward of vertical.
  // MEASURED off the built mesh (`__preview.bounds()`), not taken from the
  // sketch, which is the discipline `worker.ts` and `finisher.ts` both record
  // learning the hard way. Width is the cape at 2 x 0.33; length is the cape's
  // hem behind him against his hands on the board in front; height is the crown
  // of the hat, which the head pivot's 0.34 rad rest tips slightly forward.
  footprint: { width: 0.66, height: 1.8, length: 0.68 },
  create: build,
};
